import { t } from 'i18next';
import * as React from 'react';
import Button from '../../../../../components/Button/Button';
import { getState } from '../../../../../reduxService';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { EProjectType, UnitOfMeasure } from '../../../../enum/project';
import gridImage from '../../../../images/grid.svg';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { ISlopedGabionResults, ISteppedGabionResults, IVerticalDropsResults } from '../../../api/IApiResponse';
import { selectResults } from '../../../selector/conversion';
import { IChannelGeometrySlopedWeirForm, IChannelGeometrySteppedWeirForm, IChannelGeometryVerticalDropForm } from '../../InputForm/ChannelGeometry/IChannelGeometry';
import { IStillingBasinSteppedWeirSlopedWeirForm, IStillingBasinVerticalDropForm } from '../../InputForm/StillingBasin/IStillingBasin';
import { IWeirGeometrySlopedWeirForm, IWeirGeometrySteppedWeirForm, IWeirGeometryVerticalDropForm } from '../../InputForm/WeirGeometry/IWeirGeometry';
import { IWingsGeometrySlopedWeirForm, IWingsGeometrySteppedWeirForm, IWingsGeometryVerticalDropForm } from '../../InputForm/WingsGeometry/IWingsGeometry';
import './CrossSection.scss';
import SlopedWeirCross from './slopedWeir/SlopedWeirCross';
import SlopedWeirLongitudinal from './slopedWeir/SlopedWeirLongitudinal';
import SteppedWeirCross from './steppedWeir/SteppedWeirCross';
import SteppedWeirLongitudinal from './steppedWeir/SteppedWeirLongitudinal';
import VerticalDropCross from './verticalDrop/VerticalDropCross';
import VerticalDropLongitudinal from './verticalDrop/VerticalDropLongitudinal';
import Draggable from 'react-draggable';

const defaultScale = 70;
const defaultOffset = 125;
const minScale = 0;

const defaultCrossScale = 0;
const defaultCrossOffset = 70;
const minCrossScale = -210;
const maxCrossScale = 280;

interface ICrossSection {
    fromBackOffice?: boolean;
}

const CrossSection = (props: ICrossSection) => {
    let mousePosition;
    let offset = [0, 0];
    let isDown = false;

    const projectInfo = useAppSelector(selectProjectInfo);
    const project = getState().macra2.project;
    const result = getState().macra2.results;

    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    let canvasLongitudinal: JSX.Element;
    let canvasCross: JSX.Element;

    const canvasEl = React.useRef<HTMLDivElement>(null);

    const [scale, setScale] = React.useState<number>(defaultScale);
    const [crossScale, setCrossScale] = React.useState<number>(defaultCrossScale);
    const [showSection, setShowSection] = React.useState<boolean>(!props.fromBackOffice);

    let widthCanvas: number;
    let heightCanvas: number;

    const handleZoom = (offset: number) => {
        let _scale = scale + offset * -0.1;
        if (_scale > minScale) {
            setScale(_scale);
        }
    };

    let type = projectInfo.data!.type;

    const handleCrossZoom = (value: number) => {
        if ((crossScale < maxCrossScale && crossScale > minCrossScale) || (crossScale == maxCrossScale && value < 0) || (crossScale == minCrossScale && value > 0)) {
            setCrossScale(crossScale + value);
        }
    };

    const handleInspectAttribute = (width: number, height: number) => {
        widthCanvas = width;
        heightCanvas = height;
    };

    switch (type) {
        case EProjectType.Macra2 | EProjectType.VerticalDrop:
            canvasLongitudinal = (
                <VerticalDropLongitudinal
                    weirGeometry={project.data!.weirGeometry! as IWeirGeometryVerticalDropForm}
                    stillingBasin={project.data!.stillingBasin as IStillingBasinVerticalDropForm}
                    result={result.data as IVerticalDropsResults}
                    inspectAttribute={handleInspectAttribute}
                    scale={scale}
                    isImperial={isImperial}
                />
            );
            canvasCross = (
                <VerticalDropCross
                    wingsGeometry={project.data!.wingsGeometry as IWingsGeometryVerticalDropForm}
                    riverGeometry={project.data?.geometry as IChannelGeometryVerticalDropForm}
                    weirGeometry={project.data!.weirGeometry! as IWeirGeometryVerticalDropForm}
                    result={result.data as IVerticalDropsResults}
                    scale={crossScale}
                    isImperial={isImperial}
                />
            );
            break;
        case EProjectType.Macra2 | EProjectType.SlopedWeir:
            canvasLongitudinal = (
                <SlopedWeirLongitudinal
                    wingsGeometry={project.data!.wingsGeometry as IWingsGeometrySlopedWeirForm}
                    weirGeometry={project.data!.weirGeometry as IWeirGeometrySlopedWeirForm}
                    stillingBasin={project.data!.stillingBasin as IStillingBasinSteppedWeirSlopedWeirForm}
                    result={result.data as ISlopedGabionResults}
                    scale={scale}
                    inspectAttribute={handleInspectAttribute}
                    isImperial={isImperial}
                />
            );
            canvasCross = (
                <SlopedWeirCross
                    wingsGeometry={project.data!.wingsGeometry as IWingsGeometrySlopedWeirForm}
                    weirGeometry={project.data!.weirGeometry as IWeirGeometrySlopedWeirForm}
                    riverGeometry={project.data!.geometry as IChannelGeometrySlopedWeirForm}
                    result={result.data as ISlopedGabionResults}
                    scale={crossScale}
                    isImperial={isImperial}
                />
            );
            break;
        case EProjectType.Macra2 | EProjectType.SteppedWeir:
            canvasLongitudinal = (
                <SteppedWeirLongitudinal
                    weirGeometry={project.data!.weirGeometry as IWeirGeometrySteppedWeirForm}
                    stillingBasin={project.data!.stillingBasin as IStillingBasinSteppedWeirSlopedWeirForm}
                    wingsGeometry={project.data!.wingsGeometry as IWingsGeometrySteppedWeirForm}
                    result={result.data as ISteppedGabionResults}
                    inspectAttribute={handleInspectAttribute}
                    scale={scale}
                    isImperial={isImperial}
                />
            );
            canvasCross = (
                <SteppedWeirCross
                    wingsGeometry={project.data!.wingsGeometry as IWingsGeometrySteppedWeirForm}
                    weirGeometry={project.data!.weirGeometry as IWeirGeometrySteppedWeirForm}
                    riverGeometry={project.data!.geometry as IChannelGeometrySteppedWeirForm}
                    result={result.data as ISteppedGabionResults}
                    scale={crossScale}
                    isImperial={isImperial}
                />
            );
            break;
        default:
            canvasLongitudinal = (
                <SteppedWeirLongitudinal
                    weirGeometry={project.data!.weirGeometry as IWeirGeometrySteppedWeirForm}
                    stillingBasin={project.data!.stillingBasin as IStillingBasinSteppedWeirSlopedWeirForm}
                    wingsGeometry={project.data!.wingsGeometry as IWingsGeometrySteppedWeirForm}
                    result={result.data as ISteppedGabionResults}
                    inspectAttribute={handleInspectAttribute}
                    scale={scale}
                    isImperial={isImperial}
                />
            );
            canvasCross = (
                <SteppedWeirCross
                    wingsGeometry={project.data!.wingsGeometry as IWingsGeometrySteppedWeirForm}
                    weirGeometry={project.data!.weirGeometry as IWeirGeometrySteppedWeirForm}
                    riverGeometry={project.data!.geometry as IChannelGeometrySteppedWeirForm}
                    result={result.data as ISteppedGabionResults}
                    scale={crossScale}
                    isImperial={isImperial}
                />
            );
            break;
    }

    const handleAutoscale = () => {
        let scaleWidth = 0;
        let scaleHeight = 0;
        let offsetHeight = 4;
        let offsetWidth = 6;

        scaleWidth = canvasEl.current!.clientWidth / (widthCanvas + offsetWidth);
        scaleHeight = canvasEl.current!.clientHeight / (heightCanvas + offsetHeight);
        setScale(Math.min(scaleWidth, scaleHeight));
    };

    React.useEffect(() => handleAutoscale(), [result.data]);

    React.useEffect(() => {
        canvasEl.current!.addEventListener(
            'mousedown',
            e => {
                isDown = true;
                offset = [canvasEl.current!.offsetLeft - e.clientX, canvasEl.current!.offsetTop - e.clientY];
            },
            true,
        );

        canvasEl.current!.addEventListener(
            'mouseup',
            e => {
                isDown = false;
            },
            true,
        );

        canvasEl.current!.addEventListener(
            'mousemove',
            e => {
                e.preventDefault();
                if (isDown) {
                    mousePosition = {
                        x: e.clientX,
                        y: e.clientY,
                    };
                    canvasEl.current!.style.left = mousePosition.x + offset[0] + 'px';
                    canvasEl.current!.style.top = mousePosition.y + offset[1] + 'px';
                }
            },
            true,
        );
    }, []);

    return (
        <>
            <div onWheel={e => handleZoom(e.deltaY)} className='cross-section-container' style={{ background: `repeat url(${gridImage})` }}>
                <div className='position-absolute w-100 d-flex align-items-center justify-content-center h-100' ref={canvasEl}>
                    {canvasLongitudinal}
                </div>
            </div>
            {!props.fromBackOffice && (
                <>
                    <Draggable>
                        <div className={`canvas-cross-container ${showSection ? '' : 'd-none'}`}>{canvasCross}</div>
                    </Draggable>
                    <div className='show-button-cross-section-container'>
                        <div className='cross-action'>
                            <div className='cross-zoom-title my-2'>{t('cross_section_hide_front_view')}</div>
                            <div className='d-flex'>
                                <Button disabled={!showSection} onClick={() => handleCrossZoom(defaultCrossOffset)}>
                                    <i className='fa-solid fa-plus fa-lg'></i>
                                </Button>
                                <Button disabled={!showSection} onClick={() => handleCrossZoom(-defaultCrossOffset)}>
                                    <i className='fa-solid fa-minus fa-lg'></i>
                                </Button>
                                <Button onClick={() => setShowSection(!showSection)}>{showSection ? <i className='fa-solid fa-eye-slash' /> : <i className='fa-solid fa-eye' />}</Button>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <div className='action-cross-section-container'>
                <Button onClick={() => handleZoom(-defaultOffset)}>
                    <i className='fa-solid fa-plus fa-lg'></i>
                </Button>
                <Button onClick={() => handleZoom(+defaultOffset)}>
                    <i className='fa-solid fa-minus fa-lg'></i>
                </Button>
                <Button onClick={handleAutoscale}>
                    <i className='fa-solid fa-arrows-left-right-to-line fa-sm' />
                </Button>
            </div>
        </>
    );
};

export default CrossSection;
